import React from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { Button } from '@wf-common/atoms'

import { withDataAttributes } from '../../util/with-data-attributes'
import Dialog from './dialog'

import styles from './confirm-dialog.module.css'

const noop = Function.prototype

const propTypes = {
    aria: PropTypes.shape({
        describedby: PropTypes.string,
        labelledby: PropTypes.string
    }),
    ariaHideApp: PropTypes.bool,
    bodyOpenClassName: PropTypes.string,
    cancelButton: PropTypes.string,
    cancelButtonData: PropTypes.shape({
        data: PropTypes.string
    }),
    children: PropTypes.node,
    className: PropTypes.string,
    closeFirst: PropTypes.bool,
    closeTimeoutMS: PropTypes.number,
    confirmButton: PropTypes.string,
    confirmButtonData: PropTypes.shape({
        data: PropTypes.string
    }),
    contentRef: PropTypes.func,
    destructive: PropTypes.bool,
    htmlOpenClassName: PropTypes.string,
    icon: PropTypes.element,
    isOpen: PropTypes.bool,
    maxWidth: PropTypes.bool,
    message: PropTypes.node.isRequired,
    minWidth: PropTypes.bool,
    onAfterOpen: PropTypes.func,
    onCancel: PropTypes.func,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    onRequestClose: PropTypes.func,
    overlayClassName: PropTypes.string,
    overlayRef: PropTypes.func,
    parentSelector: PropTypes.func,
    portalClassName: PropTypes.string,
    role: PropTypes.string,
    shouldCloseOnEsc: PropTypes.bool,
    shouldCloseOnOverlayClick: PropTypes.bool,
    shouldFocusAfterRender: PropTypes.bool,
    shouldReturnFocusAfterClose: PropTypes.bool,
    style: PropTypes.shape({
        // eslint-disable-next-line react/forbid-prop-types
        content: PropTypes.objectOf(PropTypes.any),
        // eslint-disable-next-line react/forbid-prop-types
        overlay: PropTypes.objectOf(PropTypes.any)
    }),
    textClassName: PropTypes.string,
    title: PropTypes.string
}

const defaultProps = {
    cancelButton: '',
    closeFirst: false,
    confirmButton: '',
    destructive: false,
    isOpen: false,
    maxWidth: true,
    onCancel: noop,
    onClose: noop,
    onConfirm: noop
}

/**
 * Uses <Dialog /> to create a standardized confirmation dialog with confirm/cancel-buttons.
 *
 * @see Dialog
 * @see ModalBox
 * @see {@link https://github.com/reactjs/react-modal}
 *
 * @example <caption>Basic usage</caption>
 * <code>
 *     <ConfirmDialog
 *          title={'Please confirm'}
 *          message={'Are you sure?'}
 *          onConfirm={() => { alert('lets go then!') }} />
 * </code>
 *
 * @param {Object} props - The current component properties
 * @param {Object} [props.aria] - Directly passed to ModalBox
 * @param {boolean} [props.ariaHideApp] - Directly passed to ModalBox
 * @param {string} [props.bodyOpenClassName] - Directly passed to ModalBox
 * @param {string} [props.cancelButton=''] - Text on the cancel button
 * @param {ReactElement} [props.children] - The component childs
 * @param {string} [props.className] - Merging optional classname for wrapper
 * @param {boolean} [props.closeFirst=false] - Should the dialog be closed before other callbacks get fired?
 * @param {number} [props.closeTimeoutMS] - Directly passed to ModalBox
 * @param {string} [props.confirmButton=''] - Text on the confirmation button
 * @param {Object} [props.cancelButtonData] - Data for data attribute on cancel button
 * @param {Object} [props.confirmButtonData] - Data for data attribute on confirm button
 * @param {Function} [props.contentRef] - Directly passed to ModalBox
 * @param {boolean} [props.destructive=false] - Is the confirmation button a destructive one?
 * @param {string} [props.htmlOpenClassName] - Directly passed to ModalBox
 * @param {ReactElement} [props.icon] - The single icon component to be displayed
 * @param {boolean} [props.isOpen=false] - Whether the modal is visible or not
 * @param {boolean} [props.maxWidth=true] - Whether the width is limitted or not
 * @param {ReactElement} props.message - The text specifying what to confirm
 * @param {boolean} [props.minWidth=true] - Whether the content has a minimial width
 * @param {Function} [props.onAfterOpen] -  Directly passed to ModalBox
 * @param {Function} [props.onCancel=noop] - Callback to fire when the user cancels
 * @param {Function} [props.onClose=noop] - Callback to fire when the user closes the dialog
 * @param {Function} [props.onConfirm=noop] - Callback to fire when the user confirms
 * @param {Function} [props.onRequestClose] -  Directly passed to ModalBox
 * @param {string} [props.overlayClassName] - Directly passed to ModalBox
 * @param {Function} [props.overlayRef] - Directly passed to ModalBox
 * @param {Function} [props.parentSelector] - Directly passed to ModalBox
 * @param {string} [props.portalClassName] - Directly passed to ModalBox
 * @param {string} [props.role] - Directly passed to ModalBox
 * @param {boolean} [props.shouldCloseOnEsc] - Directly passed to ModalBox
 * @param {boolean} [props.shouldCloseOnOverlayClick] - Directly passed to ModalBox
 * @param {boolean} [props.shouldFocusAfterRender] - Directly passed to ModalBox
 * @param {boolean} [props.shouldReturnFocusAfterClose] - Directly passed to ModalBox
 * @param {Object} [props.style] - Directly passed to ModalBox
 * @param {string} [props.textClassName] - Merging optional classname for text
 * @param {string} [props.title] - The title to be displayed, also used for contentLabel
 * @returns {ReactElement} The modal markup
 */
function ConfirmDialog(props) {
    const {
        aria,
        ariaHideApp,
        bodyOpenClassName,
        cancelButton,
        cancelButtonData,
        children,
        className,
        closeFirst,
        closeTimeoutMS,
        confirmButton,
        confirmButtonData,
        contentRef,
        destructive,
        htmlOpenClassName,
        icon,
        isOpen,
        maxWidth,
        message,
        minWidth,
        onAfterOpen,
        onCancel,
        onClose,
        onConfirm,
        onRequestClose,
        overlayClassName,
        overlayRef,
        parentSelector,
        portalClassName,
        role,
        shouldCloseOnEsc,
        shouldCloseOnOverlayClick,
        shouldFocusAfterRender,
        shouldReturnFocusAfterClose,
        style,
        textClassName,
        title
    } = props

    const executeAndClose = func => event => {
        if (closeFirst) {
            onClose()
        }
        if (func) {
            func()
        }
        if (!closeFirst) {
            onClose()
        }

        event && event.stopPropagation()
    }

    const confirmStyle = {
        destructive: destructive,
        primary: !destructive
    }

    const ConfirmButton = withDataAttributes(Button)
    const CancelButton = withDataAttributes(Button)

    return (
        <Dialog
            {...{
                aria,
                ariaHideApp,
                bodyOpenClassName,
                children,
                className,
                closeTimeoutMS,
                contentRef,
                htmlOpenClassName,
                icon,
                isOpen,
                maxWidth,
                message,
                minWidth,
                onAfterOpen,
                onRequestClose,
                overlayClassName,
                overlayRef,
                parentSelector,
                portalClassName,
                role,
                shouldCloseOnEsc,
                shouldCloseOnOverlayClick,
                shouldFocusAfterRender,
                shouldReturnFocusAfterClose,
                style,
                textClassName,
                title
            }}
        >
            <nav className={styles['buttons']}>
                <CancelButton
                    className={cx(styles['button'], 't3sel-confirm-dialog-cancel')}
                    data={cancelButtonData}
                    onClick={executeAndClose(onCancel)}
                >
                    {cancelButton}
                </CancelButton>
                <ConfirmButton
                    className={cx(styles['button'], 't3sel-confirm-dialog-confirm')}
                    data={confirmButtonData}
                    {...confirmStyle}
                    onClick={executeAndClose(onConfirm)}
                >
                    {confirmButton}
                </ConfirmButton>
            </nav>
        </Dialog>
    )
}

ConfirmDialog.propTypes = propTypes
ConfirmDialog.defaultProps = defaultProps

export default ConfirmDialog
